import { Button, Dialog, Icon, Sheet, useIsMobile } from '@forecastify/ui';
import { useT, type Translate } from '../../i18n';
import { usePageHeader } from '../page-header';
import { Breadcrumb, type Crumb } from '../breadcrumb';
import { TaskDetail, type TaskDetailData, type TaskDetailHandlers } from './task-detail';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️ THE TRI-MODAL DETAIL: drawer, modal, full page — one body, three frames. │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * `chunks.md` calls it *"the tri-modal detail"*, and the mockup's own arrangement is the mechanism:
 * one `TaskDetail` rendered inside three containers, with a `wide` flag choosing the layout.
 *
 * Every number here is measured out of `markup.html` rather than chosen:
 *
 *  * **drawer** — a `Sheet` at `min(560px, 96vw)`, narrow layout (2577–2581);
 *  * **modal** — a bare `Dialog` at 1060px with its own 48px header carrying minimise, maximise and
 *    close, wide layout (2583–2601);
 *  * **full page** — a route, `max-w-[1160px]`, wide layout (1268–1273).
 *
 * ⚠️ **The three modes are one state, and the transitions between them are the feature.** A drawer
 * that could only be closed, rather than grown into a modal or a page, is three separate screens
 * wearing a costume. `mode` lives with the caller so a deep link can open straight into `full`.
 */
export type TaskMode = 'drawer' | 'modal' | 'full';

export interface TaskSurfaceProps {
  readonly mode: TaskMode;
  readonly open: boolean;
  readonly data: TaskDetailData;
  readonly handlers: TaskDetailHandlers;
  readonly onModeChange: (mode: TaskMode) => void;
  readonly onClose: () => void;
  /**
   * ⚠️ **A WORKSPACE-RELATIVE path, for the breadcrumb.** R-38: the caller supplies the first
   * segment, which is why this takes a path rather than the screen calling `useWorkspaceNavigate`
   * itself — the drawer and the modal render inside screens that already own their navigation.
   *
   * Absent means the trail is plain text: right for a surface with nowhere to send somebody.
   */
  readonly onNavigate?: (path: string) => void;
}

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ⚠️⚠️ THE TRAIL, BUILT ONCE FOR ALL THREE FRAMES — item 2, second pass.    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * 14 Sep gave the breadcrumb to the full page and left the drawer and the modal with
 * `KEY · title`, which is the exact failure this file's own header warns about: *"three copies of
 * a screen this size is three places for a field to be added to two of them"*. The body was one
 * component and a `wide` flag from the start; the chrome around it was not, and it drifted within
 * a day.
 *
 * ⚠️ **So the trail is a function, not markup.** Each frame renders it in its own bar — the page in
 * the shell's header, the modal in the strip it draws, the drawer as the `Sheet`'s title — and
 * there is one place where *where am I* is decided.
 *
 * ⚠️ **No project segment when the task has no project.** An *Internal* crumb pointing at nothing
 * would be a link that fails, and `projectId` is nullable for exactly that case.
 */
function taskCrumbs(
  task: TaskDetailData['task'],
  t: Translate,
  onNavigate?: (path: string) => void,
): Crumb[] {
  return [
    { label: t('breadcrumb.home'), onClick: () => onNavigate?.('/home') },
    { label: t('title.projects'), onClick: () => onNavigate?.('/projects') },
    ...(task.projectId === null
      ? []
      : [
          {
            label: task.projectName ?? t('projects.internal'),
            onClick: () => onNavigate?.(`/projects/${task.projectId ?? ''}`),
          },
        ]),
    { label: task.key },
  ];
}

export function TaskSurface(props: TaskSurfaceProps) {
  const mobile = useIsMobile();
  if (props.mode === 'full') return <FullPage {...props} />;
  /* ⚠️ 17 Oct 2026 · on a phone a modal is the full-screen drawer — the product owner. */
  if (props.mode === 'modal' && !mobile) return <Modal {...props} />;
  return <Drawer {...props} />;
}

function Drawer({ open, data, handlers, onModeChange, onClose, onNavigate }: TaskSurfaceProps) {
  const t = useT();

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      /**
       * ⚠️ **The trail IS the title** — item 2, second pass. `Sheet.title` takes a node, and the
       * question a drawer answers worst is *where did this come from*: it floats over whatever list
       * you were reading, so the one bar it has should say.
       *
       * ⚠️ **No copy button here.** The drawer is 560px and the trail already fills it; the key is
       * the last segment and the modal and the page both offer the copy.
       */
      title={<Breadcrumb crumbs={taskCrumbs(data.task, t, onNavigate)} />}
      width="min(560px, 96vw)"
      closeLabel={t('common.close')}
      /* 17 Oct 2026 · no mode buttons on a phone: the drawer is the only frame there. */
      actions={
        useIsMobile()
          ? []
          : [
              {
                label: t('task.mode.modal'),
                icon: 'maximize',
                onSelect: () => {
                  onModeChange('modal');
                },
              },
            ]
      }
    >
      {/* ⚠️ `wide={false}` — the narrow layout stacks main, sidebar and activity. */}
      <TaskDetail data={data} handlers={handlers} wide={false} />
    </Sheet>
  );
}

/**
 * ⚠️ **`bare`, with a header this file draws.** The mockup does the same: a 48px strip carrying the
 * key, the title, and the three mode controls. A `Dialog`'s own header would give a title and a
 * close button and nowhere to put minimise and maximise.
 */
function Modal({ open, data, handlers, onModeChange, onClose, onNavigate }: TaskSurfaceProps) {
  const t = useT();

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      bare
      /* ⚠️ Item 36 · the same width as the contract and line-item modals. A detail modal that is
         narrower than its siblings is a surface somebody has to re-learn. */
      width="min(1320px, 94vw)"
      closeLabel={t('common.close')}
    >
      <div className="flex h-12 shrink-0 items-center gap-2 border-b px-4">
        {/* ⚠️ The same trail the page and the drawer draw — `taskCrumbs`. The title is gone from
            the bar because it is the first thing in the body, at 22px, three centimetres below. */}
        <Breadcrumb copyValue={data.task.key} crumbs={taskCrumbs(data.task, t, onNavigate)} />

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.full')}
          title={t('task.mode.full')}
          onClick={() => {
            onModeChange('full');
          }}
        >
          <Icon name="maximize" size={15} />
        </Button>
        <Button variant="ghost" size="icon-sm" aria-label={t('common.close')} onClick={onClose}>
          <Icon name="x" size={15} />
        </Button>
      </div>

      {/* ⚠️ A flex column the detail FILLS rather than the scroller — 14 Oct. The detail's header
          stays put and its two halves scroll apart (`DetailFrame` in `detail-chrome.tsx`); the
          overflow is only the fallback for a body that is not bounded. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        <TaskDetail data={data} handlers={handlers} wide />
      </div>
    </Dialog>
  );
}

/**
 * ⚠️⚠️ **THE FULL PAGE CLAIMS THE SHELL'S HEADER; IT USED TO DRAW A SECOND ONE UNDER IT.** The
 * chrome already renders a 48px bar with the route's title in it, so a task opened as a page showed
 * *Feladat* on one line and `HSO-4 · Story clustering…` on the next — two headers, ten pixels apart,
 * and the mockup has exactly one.
 *
 * ⚠️ **Only this frame claims it.** The drawer's title belongs to the `Sheet` and the modal's to its
 * own bar; both float over a screen whose header describes something else, and taking the shell's
 * bar from them would relabel the list underneath.
 *
 * `markup.html` 160–166 is the contract: back, the key in mono at 12.5px, the title muted and
 * truncating, then the mode control on the right.
 */
function FullPage({ data, handlers, onModeChange, onClose, onNavigate }: TaskSurfaceProps) {
  const t = useT();

  usePageHeader(
    () => (
      <>
        <Button
          variant="ghost"
          size="icon-sm"
          className="-ml-1.5"
          aria-label={t('task.action.back')}
          onClick={onClose}
        >
          <Icon name="chevron-left" size={16} />
        </Button>

        {/**
         * ⚠️⚠️ **A TRAIL, WHERE THIS WAS `KEY · title`.** The old header answered *what* — which the
         * page below it answers at 22px anyway — and never *where*. A task is reached from a board,
         * a list, a search, a timeline and a pasted link, and once you are there all five look the
         * same.
         *
         * ⚠️ **The title is gone from the header entirely**, because it is the first thing in the
         * body. The trail ends at the KEY, which is the one fact about a task that is not already
         * on screen and the one people quote at each other.
         *
         * ⚠️ **No project segment when the task has no project.** An *Internal* crumb pointing at
         * nothing would be a link that fails, and `projectId` is nullable for exactly that case.
         */}
        <Breadcrumb copyValue={data.task.key} crumbs={taskCrumbs(data.task, t, onNavigate)} />

        <div className="flex-1" />

        <Button
          variant="ghost"
          size="icon-sm"
          aria-label={t('task.mode.drawer')}
          title={t('task.mode.drawer')}
          onClick={() => {
            onModeChange('drawer');
          }}
        >
          <Icon name="minimize" size={15} />
        </Button>
      </>
    ),
    /* ⚠️ **VALUES ONLY.** `onClose` and `onModeChange` are inline arrows at the call site, so
     * including them would re-run this effect on every render and freeze the app — the loop
     * `page-header.tsx` records at length. The builder reaches them through the ref. */
    [data.task.key, data.task.title, data.task.projectId, data.task.projectName],
  );

  return (
    <div data-screen="task" className="flex min-h-0 flex-1 flex-col">
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {/* 1160px, from `markup.html`. */}
        <div className="mx-auto flex min-h-0 w-full max-w-[1160px] flex-1 flex-col">
          <TaskDetail data={data} handlers={handlers} wide />
        </div>
      </div>
    </div>
  );
}
